import { useDispatch, useSelector } from "react-redux";
import { useCRMDataClient } from "@/services/data-client/DataClientContext";
import {
  setMarkAsRead,
  setMarkAllAsRead,
  setNotificationToState,
  setPageInfo,
  getStaticExportName,
} from "@/store/slices/notifications/notificationsSlice";
import { toast } from "react-toastify";
import { ToastTemplate } from "@/services/variables";
import { useEffect } from "react";
import useSignalRClient from "@/services/signal-R/signalr-hubs";
import useFrontdoorSignalR from "@/services/signal-R/useFrontdoorSignalR";
import Lead_signalr from "@/modules/crm/leads/signalr-constants";
import Notification_signalr from "@/services/signal-R/notification-signalr-constants";
import {
  getAllRequests,
  setRequestToState,
  unsetRequestFromState,
} from "@/store/slices/requests";
import {
  handleLeadExportNotification,
  parseLeadExportData,
} from "@/services/leads/lead-export-notification";
import { exportLeads } from "@/store/slices/lead/leadSlice";
import useDesktopNotification from "./useDesktopNotification";
import { appointmentNotificationTypes } from "@/store/slices/notifications/types";
import { faCalendar } from "@fortawesome/free-regular-svg-icons";
import guid from "guid";

/**
 * ──────────────────────────────────────────────────────────────
 *  NOTIFICATION HUB SWITCH
 *
 *  Set to true  → use FrontDoor SignalR hub for live notifications
 *  Set to false → fallback to legacy leadhub listeners
 * ──────────────────────────────────────────────────────────────
 */
const USE_FRONTDOOR_HUB = true;

/**
 * useFrontdoorNotifications
 *
 * A replacement hook for useLeadNotifications that uses the Frontdoor API.
 * Handles fetching, pagination sync, marking as read, and real-time SignalR updates.
 *
 * Supports switching between FrontDoor hub and legacy leadhub via USE_FRONTDOOR_HUB flag.
 */
const useFrontdoorNotifications = (prop = {}) => {
  const dispatch = useDispatch();
  const { client: dataClient } = useCRMDataClient();
  const profile = useSelector((state) => state?.auth?.profile);
  const requests = useSelector(getAllRequests);
  const { showNotification } = useDesktopNotification();

  // ── Legacy leadhub setup (used when USE_FRONTDOOR_HUB = false) ──
  const {
    hubName: leadHubName,
    InvokeMethodNames: {
      markAllAsRead: legacyMarkAllAsReadMethod,
      markNotificationAsRead: legacyMarkAsReadMethod,
    },
    onReceiveMethodNames: {
      onNotificationArrived: legacyOnNotificationArrived,
      onUpdateNotificationResponse: legacyOnUpdateNotification,
    },
  } = Lead_signalr;

  const { statuses: leadStatuses, RTC: leadRTC } = useSignalRClient({
    hubName: leadHubName,
  });
  const leadhub = leadRTC[leadHubName];

  // ── FrontDoor hub setup (used when USE_FRONTDOOR_HUB = true) ──
  const { hub: frontdoorHub, connected: frontdoorConnected } =
    useFrontdoorSignalR();

  const {
    InvokeMethodNames: {
      markAllAsRead: fdMarkAllAsReadMethod,
      markAsRead: fdMarkAsReadMethod,
    },
    onReceiveMethodNames: {
      onNotificationArrived: fdOnNotificationArrived,
      onNotificationUpdated: fdOnNotificationUpdated,
    },
  } = Notification_signalr;

  // ── Resolve the active hub and method names based on the switch ──
  const activeHub = USE_FRONTDOOR_HUB ? frontdoorHub : leadhub;
  const markAsReadMethod = USE_FRONTDOOR_HUB
    ? fdMarkAsReadMethod
    : legacyMarkAsReadMethod;
  const markAllAsReadMethod = USE_FRONTDOOR_HUB
    ? fdMarkAllAsReadMethod
    : legacyMarkAllAsReadMethod;
  const onNotificationArrivedMethod = USE_FRONTDOOR_HUB
    ? fdOnNotificationArrived
    : legacyOnNotificationArrived;
  const onUpdateNotificationMethod = USE_FRONTDOOR_HUB
    ? fdOnNotificationUpdated
    : legacyOnUpdateNotification;

  // ── Register FrontDoor hub listeners (only when USE_FRONTDOOR_HUB = true) ──
  useEffect(() => {
    if (!USE_FRONTDOOR_HUB || !frontdoorHub || !frontdoorConnected) return;

    const methodNameLower = fdOnNotificationArrived.toLowerCase();
    const activeListeners = Object.keys(frontdoorHub._methods || {});

    if (!activeListeners.includes(methodNameLower)) {
      frontdoorHub.on(fdOnNotificationArrived, (response) => {
        console.log(`[FrontdoorHub] ${fdOnNotificationArrived}::`, response);
        dispatch(
          setRequestToState({ key: fdOnNotificationArrived, data: response })
        );
      });
    }

    const updateMethodLower = fdOnNotificationUpdated.toLowerCase();
    if (!activeListeners.includes(updateMethodLower)) {
      frontdoorHub.on(fdOnNotificationUpdated, (response) => {
        console.log(`[FrontdoorHub] ${fdOnNotificationUpdated}::`, response);
        dispatch(
          setRequestToState({ key: fdOnNotificationUpdated, data: response })
        );
      });
    }
  }, [frontdoorHub, frontdoorConnected]);

  // ── Notification arrived listener (works for both hubs via requests store) ──
  useEffect(() => {
    let response = requests[onNotificationArrivedMethod];
    if (response) {
      console.log(
        "[useFrontdoorNotifications] Notification arrived::",
        response
      );
      const payload = JSON.parse(
        JSON.stringify(response.notification || response)
      );
      const exportResult = handleLeadExportNotification(payload);
      if (exportResult.handled) {
        dispatch(
          unsetRequestFromState({ keys: [onNotificationArrivedMethod] })
        );
        if (exportResult.needsFallback) {
          const filename = exportResult.exportData;
          const displayName = getStaticExportName(
            filename,
            exportResult.isReport
          );

          if (filename) {
            exportLeads(
              filename,
              true,
              displayName,
              exportResult.isReport
            ).catch((err) => {
              console.error("Failed to download report export:", err);
            });
          }
        }
      }

      if (prop?.notify) {
        prop?.notify(payload);
      }

      if (
        prop?.desktopNotification &&
        appointmentNotificationTypes.reminder.includes(payload.type)
      ) {
        showNotification({
          title: payload.title,
          body: payload.description,
          icon: faCalendar,
        });
      }

      payload.loggedInUserId = profile?.UserID;

      // Filter out technical notifications if needed
      if (payload.type !== "LeadImportWaiting") {
        dispatch(setNotificationToState(payload));
      }
    }
  }, [requests[onNotificationArrivedMethod]]);

  // ── Update notification listener ──
  useEffect(() => {
    let response = requests[onUpdateNotificationMethod];
    if (response) {
      if (response.isSuccess) {
        dispatch(setMarkAsRead(response.notification));
      }
    }
  }, [requests[onUpdateNotificationMethod]]);

  /**
   * Fetch notifications from Frontdoor API.
   * Maps results to Redux pageInfo and returns the item list.
   */
  const getNotifications = async (payload) => {
    if (!profile?.UserGUID) {
      return [];
    }

    try {
      const { skip = 0, take = 10 } = payload || {};
      const page = Math.floor(skip / take) + 1;

      const response = await dataClient.notifications.getByUser(
        profile?.UserGUID,
        {
          page,
          perPage: take,
        }
      );

      const items =
        response?.items ||
        response?.data ||
        (Array.isArray(response) ? response : []);
      const recordsCount = items.length;

      dispatch(
        setPageInfo({
          recordsCount,
          take,
          hasNextPage: recordsCount >= take,
        })
      );

      return items;
    } catch (error) {
      console.error(
        "[useFrontdoorNotifications] getNotifications Error:",
        error
      );
      toast.error(
        ToastTemplate.error(error.message || "Failed to fetch notifications")
      );
      return [];
    }
  };

  /**
   * Mark a specific notification as read.
   */
  const setNotificationIsRead = async (idOrNotification) => {
    try {
      const id =
        typeof idOrNotification === "object" && idOrNotification !== null
          ? idOrNotification.notificationId || idOrNotification.id
          : idOrNotification;
      const payload = {
        id,
        requestId: guid.raw(),
      };
      if (activeHub && activeHub.state === "Connected") {
        return activeHub.invoke(markAsReadMethod, payload);
      }
      console.warn(
        "[useFrontdoorNotifications] Hub connection not in Connected state. Skipping invoke."
      );
      return null;
    } catch (error) {
      console.error(
        "[useFrontdoorNotifications] setNotificationIsRead Error:",
        error
      );
      return null;
    }
  };

  /**
   * Mark all notifications as read via SignalR.
   * Hub method: MarkAllNotificationsAsRead(Guid userId)
   */
  const markAllAsRead = async () => {
    try {
      const userId = profile?.UserGUID;
      if (!userId) {
        console.warn(
          "[useFrontdoorNotifications] markAllAsRead skipped: missing UserGUID."
        );
        return null;
      }
      const payload = {
        userId,
        requestId: guid.raw(),
      };
      if (activeHub && activeHub.state === "Connected") {
        const result = await activeHub.invoke(markAllAsReadMethod, payload);
        dispatch(setMarkAllAsRead());
        return result ?? true;
      }
      console.warn(
        "[useFrontdoorNotifications] Hub connection not in Connected state. Skipping invoke."
      );
      return null;
    } catch (error) {
      console.error(
        "[useFrontdoorNotifications] markAllAsRead Error:",
        error
      );
      return null;
    }
  };

  return {
    getNotifications,
    setNotificationIsRead,
    markAllAsRead,
  };
};

export default useFrontdoorNotifications;
